import { useState, useEffect, useCallback } from 'react'
import { getFirestore, doc, getDoc } from 'firebase/firestore'
import { app } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import analyticsService from '../services/analyticsService'
import { usePageTracking } from '../hooks/useAnalytics'
import { getAuth } from 'firebase/auth'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts'
import AssessmentIcon from '@mui/icons-material/Assessment'
import PeopleIcon from '@mui/icons-material/People'
import SearchIcon from '@mui/icons-material/Search'
import ChatIcon from '@mui/icons-material/Chat'
import SpeedIcon from '@mui/icons-material/Speed'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import WarningIcon from '@mui/icons-material/Warning'
import WhatshotIcon from '@mui/icons-material/Whatshot'
import DevicesIcon from '@mui/icons-material/Devices'
import DownloadIcon from '@mui/icons-material/Download'
import RefreshIcon from '@mui/icons-material/Refresh'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import AutoStoriesIcon from '@mui/icons-material/AutoStories'

import './AdminDashboard.css'

function AdminDashboard () {
  const { user } = useAuth()
  usePageTracking()

  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalSearches: 0,
    avgResponseTime: 2.5,
    avgConversationLength: 0,
    totalContributions: 0,
    pendingContributions: 0,
    errorRate: 1.2,
    topSearches: [],
    deviceDistribution: [],
    usageOverTime: [],
    feedbackStats: {
      totalFeedback: 0,
      positiveFeedback: 0,
      negativeFeedback: 0,
      recentNegative: []
    },
    contentQuality: {
      noResultsRate: 0,
      noResultsCount: 0,
      totalBotResponses: 0,
      topKnowledgeArticles: [],
      analyzedConversations: 0
    }
  })

  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // KORRIGIERT: loadDashboardData auch als useCallback
  const loadDashboardData = useCallback(async () => {
    try {
      const currentUser = getAuth(app).currentUser

      if (!currentUser) {
        throw new Error('Kein authentifizierter User gefunden')
      }

      const token = await currentUser.getIdToken(true)

      // Direkter HTTP-Call
      const response = await fetch(
        'https://us-central1-altknowledge-version2.cloudfunctions.net/getAdminStats',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'HTTP Error')
      }

      const data = await response.json()

      setStats({
        ...data,
        avgResponseTime: 2.5,
        errorRate: 1.2
      })

      analyticsService.trackUserAction('dashboard_loaded', {
        total_users: data.totalUsers,
        pending_contributions: data.pendingContributions
      })

      setLoading(false)
    } catch (error) {
      console.error('Dashboard load error:', error)
      setError('Fehler beim Laden: ' + error.message)
      setLoading(false)
    }
  }, [])

  const checkAdminAndLoadData = useCallback(async () => {
    if (!user) {
      setError('Nicht angemeldet')
      setLoading(false)
      return
    }

    try {
      const db = getFirestore(app)
      const userDoc = await getDoc(doc(db, 'users', user.uid))

      if (!userDoc.exists()) {
        setError('User-Profil nicht gefunden')
        setLoading(false)
        return
      }

      const userData = userDoc.data()
      const adminStatus = ['admin', 'moderator', 'superadmin'].includes(
        userData.role
      )

      setIsAdmin(adminStatus)

      if (!adminStatus) {
        setError('Keine Admin-Berechtigung')
        setLoading(false)
        return
      }

      // Lade alle Daten (inkl. Feedback-Stats) von getAdminStats
      await loadDashboardData()
    } catch (err) {
      console.error('Admin check error:', err)
      setError('Fehler beim Prüfen der Berechtigung')
      setLoading(false)
    }
  }, [user, loadDashboardData])

  // NUR EIN useEffect!
  useEffect(() => {
    checkAdminAndLoadData()
  }, [checkAdminAndLoadData])

  if (loading) {
    return (
      <div className='dashboard'>
        <div className='loading'>Lade Dashboard...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='dashboard'>
        <div
          className='error-banner'
          style={{ margin: '2rem', padding: '1rem' }}
        >
          <h2>⚠️ Zugriff verweigert</h2>
          <p>{error}</p>
          <p>Nur Administratoren und Moderatoren können das Dashboard sehen.</p>
        </div>
      </div>
    )
  }

  return (
    <div className='dashboard'>
      <div className='dashboard-header'>
        <h1>
          <AssessmentIcon
            style={{ verticalAlign: 'middle', marginRight: '0.5rem' }}
          />
          Analytics Dashboard
        </h1>
        <p>Übersicht über Nutzung und Performance</p>
      </div>

      {/* KPIs Grid */}
      <div className='kpi-grid'>
        <div className='kpi-card'>
          <div className='kpi-icon'>
            <PeopleIcon fontSize='large' />
          </div>
          <div className='kpi-content'>
            <div className='kpi-label'>Gesamte Nutzer</div>
            <div className='kpi-value'>{stats.totalUsers}</div>
            <div className='kpi-sublabel'>
              {stats.activeUsers} aktiv (7 Tage)
            </div>
          </div>
        </div>

        <div className='kpi-card'>
          <div className='kpi-icon'>
            <SearchIcon fontSize='large' />
          </div>
          <div className='kpi-content'>
            <div className='kpi-label'>Gesamt Anfragen</div>
            <div className='kpi-value'>{stats.totalSearches}</div>
            <div className='kpi-sublabel'>Letzte 7 Tage</div>
          </div>
        </div>

        {/* Nach der "Gesamt Anfragen" Card */}
        <div className='kpi-card'>
          <div className='kpi-icon'>
            <ChatIcon fontSize='large' />
          </div>
          <div className='kpi-content'>
            <div className='kpi-label'>Ø Nachrichten/Chat</div>
            <div className='kpi-value'>{stats.avgConversationLength}</div>
            <div className='kpi-sublabel'>
              {stats.avgConversationLength > 5
                ? 'Hohe Interaktion'
                : 'Kurze Chats'}
            </div>
          </div>
        </div>

        <div className='kpi-card'>
          <div className='kpi-icon'>
            <SpeedIcon fontSize='large' />
          </div>
          <div className='kpi-content'>
            <div className='kpi-label'>Ø Antwortzeit</div>
            <div className='kpi-value'>{stats.avgResponseTime}s</div>
            <div
              className='kpi-sublabel'
              style={{
                color: stats.avgResponseTime < 3 ? '#4caf50' : '#ff9800'
              }}
            >
              {stats.avgResponseTime < 3 ? 'Gut' : 'Optimierbar'}
            </div>
          </div>
        </div>

        <div className='kpi-card'>
          <div className='kpi-icon'>
            <MenuBookIcon fontSize='large' />
          </div>
          <div className='kpi-content'>
            <div className='kpi-label'>Wissens-Beiträge</div>
            <div className='kpi-value'>{stats.totalContributions}</div>
            <div className='kpi-sublabel'>
              {stats.pendingContributions} ausstehend
            </div>
          </div>
        </div>

        <div className='kpi-card'>
          <div className='kpi-icon'>
            <WarningIcon fontSize='large' />
          </div>
          <div className='kpi-content'>
            <div className='kpi-label'>Fehlerrate</div>
            <div className='kpi-value'>{stats.errorRate}%</div>
            <div
              className='kpi-sublabel'
              style={{ color: stats.errorRate < 2 ? '#4caf50' : '#f44336' }}
            >
              {stats.errorRate < 2 ? 'Niedrig' : 'Erhöht'}
            </div>
          </div>
        </div>

        {/* Chat Feedback KPI */}
        <div className='kpi-card'>
          <div className='kpi-icon'>
            <ThumbUpIcon fontSize='large' />
          </div>
          <div className='kpi-content'>
            <div className='kpi-label'>Chat Feedback</div>
            <div className='kpi-value'>
              {stats.feedbackStats.totalFeedback > 0
                ? Math.round(
                    (stats.feedbackStats.positiveFeedback /
                      stats.feedbackStats.totalFeedback) *
                      100
                  )
                : 0}
              %
            </div>
            <div className='kpi-sublabel'>
              {stats.feedbackStats.positiveFeedback} 👍 /{' '}
              {stats.feedbackStats.negativeFeedback} 👎
            </div>
          </div>
        </div>

        {/* No Results Rate KPI */}
        <div className='kpi-card'>
          <div className='kpi-icon'>
            <ErrorOutlineIcon fontSize='large' />
          </div>
          <div className='kpi-content'>
            <div className='kpi-label'>No Results Rate</div>
            <div className='kpi-value'>
              {stats.contentQuality.noResultsRate}%
            </div>
            <div
              className='kpi-sublabel'
              style={{
                color:
                  stats.contentQuality.noResultsRate < 10
                    ? '#4caf50'
                    : stats.contentQuality.noResultsRate < 20
                    ? '#ff9800'
                    : '#f44336'
              }}
            >
              {stats.contentQuality.noResultsCount} von{' '}
              {stats.contentQuality.totalBotResponses} Anfragen
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className='charts-grid'>
        <div className='chart-card'>
          <h3>Nutzung über Zeit</h3>
          {stats.usageOverTime && stats.usageOverTime.length > 0 ? (
            <ResponsiveContainer width='100%' height={250}>
              <LineChart data={stats.usageOverTime}>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis
                  dataKey='date'
                  tick={{ fontSize: 12 }}
                  tickFormatter={date => {
                    const d = new Date(date)
                    return `${d.getDate()}.${d.getMonth() + 1}`
                  }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  labelFormatter={date =>
                    new Date(date).toLocaleDateString('de-DE')
                  }
                />
                <Line
                  type='monotone'
                  dataKey='conversations'
                  stroke='#1976d2'
                  strokeWidth={2}
                  name='Gespräche'
                />
                <Line
                  type='monotone'
                  dataKey='searches'
                  stroke='#4caf50'
                  strokeWidth={2}
                  name='Nachrichten'
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className='chart-placeholder'>
              <p>Keine Daten verfügbar</p>
            </div>
          )}
        </div>
        {/* <div className='chart-card'>
          <h3>Performance Metriken</h3>
          <div className='chart-placeholder'>
            <p>Antwortzeit-Verteilung</p>
            <p style={{ fontSize: '0.9rem', color: '#666' }}>
              Histogram der Response-Zeiten
            </p>
          </div>
        </div> */}
      </div>

      {/* Recent Activity */}
      <div className='activity-section'>
        {/* Top Suchanfragen */}
        <div className='activity-card'>
          <h3>
            <WhatshotIcon
              style={{ verticalAlign: 'middle', marginRight: '0.5rem' }}
            />
            Top Suchanfragen
          </h3>
          <div className='activity-list'>
            {stats.topSearches && stats.topSearches.length > 0 ? (
              stats.topSearches.slice(0, 5).map((item, idx) => (
                <div className='activity-item' key={idx}>
                  <span className='query'>{item.query}</span>
                  <span className='count'>{item.count}x</span>
                </div>
              ))
            ) : (
              <div
                style={{ padding: '1rem', color: '#999', textAlign: 'center' }}
              >
                Keine Daten verfügbar
              </div>
            )}
          </div>
        </div>

        {/* Geräte-Verteilung */}
        <div className='activity-card'>
          <h3>
            <DevicesIcon
              style={{ verticalAlign: 'middle', marginRight: '0.5rem' }}
            />
            Geräte-Verteilung
          </h3>
          <div className='activity-list'>
            {stats.deviceDistribution && stats.deviceDistribution.length > 0 ? (
              stats.deviceDistribution.slice(0, 5).map((item, idx) => (
                <div className='activity-item' key={idx}>
                  <span className='query'>{item.device}</span>
                  <span className='count'>{item.percentage}%</span>
                </div>
              ))
            ) : (
              <div
                style={{ padding: '1rem', color: '#999', textAlign: 'center' }}
              >
                Keine Daten verfügbar
              </div>
            )}
          </div>
        </div>

        {/* Letzte negative Feedbacks */}
        <div className='activity-card'>
          <h3>
            <ThumbDownIcon
              style={{ verticalAlign: 'middle', marginRight: '0.5rem' }}
            />
            Negatives Feedback
          </h3>
          <div className='activity-list'>
            {stats.feedbackStats.recentNegative &&
            stats.feedbackStats.recentNegative.length > 0 ? (
              stats.feedbackStats.recentNegative.map((item, idx) => (
                <div className='activity-item feedback-item' key={idx}>
                  <span
                    className='query'
                    style={{ flex: 1, fontSize: '0.85rem' }}
                  >
                    {item.message?.substring(0, 60)}
                    {item.message?.length > 60 ? '...' : ''}
                  </span>
                  <span
                    className='count'
                    style={{ fontSize: '0.75rem', color: '#999' }}
                  >
                    {item.timestamp?.toDate
                      ? new Date(item.timestamp.toDate()).toLocaleDateString(
                          'de-DE',
                          {
                            day: '2-digit',
                            month: '2-digit'
                          }
                        )
                      : 'N/A'}
                  </span>
                </div>
              ))
            ) : (
              <div
                style={{ padding: '1rem', color: '#999', textAlign: 'center' }}
              >
                Kein negatives Feedback 🎉
              </div>
            )}
          </div>
        </div>

        {/* Top Wissensartikel */}
        <div className='activity-card'>
          <h3>
            <AutoStoriesIcon
              style={{ verticalAlign: 'middle', marginRight: '0.5rem' }}
            />
            Top Wissensartikel
          </h3>
          <div className='activity-list'>
            {stats.contentQuality.topKnowledgeArticles &&
            stats.contentQuality.topKnowledgeArticles.length > 0 ? (
              stats.contentQuality.topKnowledgeArticles.map((item, idx) => (
                <div className='activity-item' key={idx}>
                  <span className='query' style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                      {item.title}
                    </div>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#999',
                        marginTop: '0.2rem'
                      }}
                    >
                      {item.device} •{' '}
                      {item.type === 'technician_knowledge'
                        ? '🔧 Techniker-Wissen'
                        : '📄 Manual'}
                    </div>
                  </span>
                  <span
                    className='count'
                    style={{
                      fontSize: '1rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {item.usageCount}x
                  </span>
                </div>
              ))
            ) : (
              <div
                style={{ padding: '1rem', color: '#999', textAlign: 'center' }}
              >
                Keine Daten verfügbar
              </div>
            )}
          </div>
          {stats.contentQuality.analyzedConversations > 0 && (
            <div
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.75rem',
                color: '#999',
                borderTop: '1px solid rgba(0,0,0,0.05)',
                marginTop: '0.5rem'
              }}
            >
              Basierend auf {stats.contentQuality.analyzedConversations}{' '}
              analysierten Gesprächen
            </div>
          )}
        </div>

        {/* <div className='activity-card'>
          <h3>⚠️ Letzte Fehler</h3>
          <div className='activity-list'>
            <div className='error-item'>
              <span className='error-type'>chat_api_error</span>
              <span className='error-time'>vor 5 Min</span>
            </div>
            <div className='error-item'>
              <span className='error-type'>slow_response</span>
              <span className='error-time'>vor 12 Min</span>
            </div>
            <div className='error-item'>
              <span className='error-type'>validation_error</span>
              <span className='error-time'>vor 25 Min</span>
            </div>
          </div>
        </div> */}
      </div>

      {/* System Health */}
      {/* <div className='health-section'>
        <h3>System Gesundheit</h3>
        <div className='health-grid'>
          <div className='health-item'>
            <div className='health-label'>Firebase</div>
            <div className='health-status healthy'>✓ Operativ</div>
          </div>
          <div className='health-item'>
            <div className='health-label'>Embeddings API</div>
            <div className='health-status healthy'>✓ Operativ</div>
          </div>
          <div className='health-item'>
            <div className='health-label'>LLM Service</div>
            <div className='health-status healthy'>✓ Operativ</div>
          </div>
          <div className='health-item'>
            <div className='health-label'>Storage</div>
            <div className='health-status healthy'>✓ Operativ</div>
          </div>
        </div>
      </div> */}

      {/* Actions */}
      <div className='dashboard-actions'>
        <button
          className='action-btn'
          onClick={() => {
            analyticsService.trackUserAction('refresh_dashboard_clicked')
            loadDashboardData()
          }}
        >
          <RefreshIcon
            style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}
          />
          Aktualisieren
        </button>
      </div>
    </div>
  )
}

export default AdminDashboard
